/* Rhody Reviews api/v1 -- reply-template domain.
   The client-approved reply library behind the approval screen (Steve,
   2026-08-16). Two bands: 'positive' (4-5 star, auto-post eligible) and
   'negative' (1-3 star, always human-approved). */
import { request } from './_http';

export type ReplyBand = 'positive' | 'negative';

export interface ReplyTemplate {
  id: string;
  client_id: string;
  band: ReplyBand;
  body: string;
  /** 'library' = one of our ten seeded lines, 'client' = the owner wrote it. */
  source: 'library' | 'client';
  approved: boolean;
  approved_at?: string | null;
  approved_by?: string | null;
  position: number;
  active: boolean;
  use_count?: number;
  last_used_at?: string | null;
}

export interface ReplyTemplatesResponse {
  client_id: string;
  auto_reply_enabled: boolean;
  auto_reply_enabled_at?: string | null;
  /** Drives whether the auto-reply switch can be turned on at all. */
  approved_positive_count: number;
  positive: ReplyTemplate[];
  negative: ReplyTemplate[];
}

export interface ReplyTemplateUpdate {
  body?: string;
  approved?: boolean;
  active?: boolean;
}

export const replyTemplatesApi = {
  list(signal?: AbortSignal): Promise<ReplyTemplatesResponse> {
    return request<ReplyTemplatesResponse>('/api/v1/reply-templates', { signal });
  },

  /** Install the twenty seed lines, unapproved. Idempotent. */
  seed(): Promise<{ seeded: number }> {
    return request<{ seeded: number }>('/api/v1/reply-templates/seed', { method: 'POST' });
  },

  /** Add a line the owner wrote. Client-authored lines arrive approved. */
  create(input: { band: ReplyBand; body: string }): Promise<ReplyTemplate> {
    return request<ReplyTemplate>('/api/v1/reply-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  update(id: string, input: ReplyTemplateUpdate): Promise<ReplyTemplate> {
    return request<ReplyTemplate>(`/api/v1/reply-templates/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /** Flip the per-client auto-reply switch. 409s if nothing is approved yet. */
  setAutoReply(enabled: boolean): Promise<{ client_id: string; auto_reply_enabled: boolean }> {
    return request<{ client_id: string; auto_reply_enabled: boolean }>(
      '/api/v1/reply-templates/auto-reply',
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    );
  },
};
